import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { helpdeskAPI } from '../services/api';

const CreateTicketModal = ({ show, onHide, onTicketCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: '',
    category: '',
    application: '', // Will be set from loaded applications
  });
  const [priorities, setPriorities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const loadFormData = useCallback(async () => {
    try {
      setLoadingData(true);
      const [prioritiesRes, categoriesRes, applicationsRes] = await Promise.all([
        helpdeskAPI.getPriorities(),
        helpdeskAPI.getCategories(),
        helpdeskAPI.getApplications(),
      ]);
      
      const loadedPriorities = prioritiesRes.data.results || prioritiesRes.data;
      const loadedCategories = categoriesRes.data.results || categoriesRes.data;
      const loadedApplications = applicationsRes.data.results || applicationsRes.data;
      
      setPriorities(loadedPriorities);
      setCategories(loadedCategories);
      setApplications(loadedApplications);
      
      // Auto-select first application if available
      if (loadedApplications.length > 0 && !formData.application) {
        setFormData(prev => ({
          ...prev,
          application: loadedApplications[0].id
        }));
      }
    } catch (err) {
      console.error('Error loading form data:', err);
      setError('Failed to load form options');
    } finally {
      setLoadingData(false);
    }
  }, [formData.application]);

  useEffect(() => {
    if (show) {
      loadFormData();
    }
  }, [show, loadFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Please enter a ticket title');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Please enter a description');
      return;
    }
    
    if (!formData.priority) {
      setError('Please select a priority');
      return;
    }
    
    if (!formData.category) {
      setError('Please select a category');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await helpdeskAPI.createTicket(formData);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        priority: '',
        category: '',
        application: applications.length > 0 ? applications[0].id : '',
      });
      
      // Notify parent component
      if (onTicketCreated) {
        onTicketCreated(response.data);
      }
      
      // Close modal
      onHide();
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(
        err.response?.data?.detail || 
        err.response?.data?.message ||
        'Failed to create ticket. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        description: '',
        priority: '',
        category: '',
        application: applications.length > 0 ? applications[0].id : '',
      });
      setError(null);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-plus-circle me-2"></i>
          Create New Ticket
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          
          {loadingData ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Loading form...</span>
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>
                  Title <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Brief description of your issue"
                  required
                  disabled={loading}
                  maxLength={200}
                />
                <Form.Text className="text-muted">
                  {formData.title.length}/200 characters
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Provide detailed information about your issue..."
                  required
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  Please include as much detail as possible to help us resolve your issue quickly.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Priority <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select priority...</option>
                  {priorities.map((priority) => (
                    <option key={priority.id} value={priority.id}>
                      {priority.name} - Response in {priority.response_time_hours}h
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Choose the urgency level for this ticket
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select category...</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select the category that best describes your issue
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || loadingData}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Creating...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Create Ticket
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;
